import {
  Button,
  BillboardButton,
  DialogOverlayActions,
  Paragraph,
  Row,
  Separator,
  useOverlayUpdate,
  useStatusOverlay,
} from '@fs/zion-ui'

import React, { useEffect, useMemo, useState } from 'react'
import { Link, useHistory } from '@fs/zion-router'
import { Trans, useTranslation } from 'react-i18next'
import ZionForm, { FormCheckbox, FormToggle, useWatch, useZionForm } from '@fs/zion-form'
import { useFeatureFlag } from '@fs/zion-flags'
import { useShowTemple } from '@fs/zion-permissions'
import { NoticeLoading } from '@fs/zion-icon'
import { useUser } from '@fs/zion-user'
import GroupImage from '../group-image/GroupImage'
import GroupDetailsForm from '../common/GroupDetailsForm'
import handleCreateGroup from '../helpers/create-edit/handleCreateGroup'
import getDefaultValues from '../helpers/create-edit/getDefaultValues'
import resolver from '../helpers/create-edit/resolver'
import { useFamilyGroupContext } from '../../../providers/FamilyGroupProvider'

const formId = 'create-group'

export default function CreateGroupRouteView({
  changeGroupImagePath,
  uploadedImage,
  overlay,
  onCreateGroup,
  updatedGroupDetails,
  setUpdatedGroupDetails,
}) {
  const [loadingCreateGroup, setLoadingCreateGroup] = useState(false)
  const updateFooter = useOverlayUpdate('footer')
  const history = useHistory()
  const [t] = useTranslation()
  const [showTemple] = useShowTemple()
  const fallbackDetails = useMemo(
    () => ({ isFallback: true, imageUrl: uploadedImage ? URL.createObjectURL(uploadedImage) : undefined }),
    [uploadedImage]
  )
  const showStatusOverlay = useStatusOverlay()
  const user = useUser()
  const { loadFamilyGroup } = useFamilyGroupContext()
  const familyGroupTreeExplanationFlag = useFeatureFlag('discovery_together_familyGroupTreeExplanation')
  const methods = useZionForm({
    mode: 'onSubmit',
    defaultValues: getDefaultValues(undefined, updatedGroupDetails),
    resolver: (values) => resolver(values, t),
  })

  const { groupName, agreeJoinCheckTree1, agreeJoinCheckTree2 } = useWatch({
    control: methods.control,
  })

  const disableSave =
    user.userLoading || loadingCreateGroup || !groupName || !agreeJoinCheckTree1 || !agreeJoinCheckTree2

  const loadingSubmitButton = loadingCreateGroup || user.userLoading

  useEffect(() => {
    if (overlay.transitioning && !overlay.isOpen) setUpdatedGroupDetails({})
  }, [overlay.isOpen, overlay.transitioning])

  useEffect(() => {
    updateFooter(
      <DialogOverlayActions>
        <Button data-testid="create-edit-group.cancel.button" onClick={overlay.close}>
          {t('common-ui:cancel.action')}
        </Button>
        <BillboardButton
          color="gray"
          type="submit"
          form={formId}
          data-testid="create-edit-group.submit.button"
          Icon={loadingSubmitButton ? NoticeLoading : undefined}
          disabled={disableSave}
          emphasis="high"
        >
          {t('create-edit-group.action.create-group')}
        </BillboardButton>
      </DialogOverlayActions>
    )
  }, [disableSave, loadingSubmitButton, overlay.close, t, updateFooter])

  return (
    <ZionForm
      id={formId}
      data-testid="create-group-overlay-form"
      methods={methods}
      onSubmit={(formValues) =>
        handleCreateGroup({
          formValues,
          setLoading: setLoadingCreateGroup,
          userCisId: user.cisId,
          uploadedImage,
          showStatusOverlay,
          t,
          onCreateGroup: async (groupValues) => {
            const isDoNotCloseOverlay = !!(await onCreateGroup?.(groupValues, history))
            await loadFamilyGroup()
            return isDoNotCloseOverlay
          },
          closeOverlay: overlay.close,
        })
      }
    >
      <Row alignX="center">
        <GroupImage
          group={fallbackDetails}
          isAdmin
          onGroupImageClick={() => {
            setUpdatedGroupDetails(methods?.getValues()) // keep altered values in state while user uploads an image
            history.push(changeGroupImagePath)
          }}
        />
      </Row>
      <Separator size="sm" />
      <GroupDetailsForm />

      {showTemple && (
        <FormToggle
          allowLabelWrapping
          name="allowOrdinancesSharingCheck"
          label={t('groups.allow-ordinances-sharing.label', 'Allow this group to share ordinances.')}
        />
      )}
      {familyGroupTreeExplanationFlag.isOn && (
        <>
          <Separator size="xs" />
          <Paragraph size="xs">
            {t(
              'groups.family-group-tree-explanation.label',
              'Family groups created in Together allow the group to share stories, memories and work together in a family group tree.'
            )}
          </Paragraph>
        </>
      )}
      <Separator size="xs" />
      <>
        <FormCheckbox
          data-testid="agree-join-checkbox-tree1"
          name="agreeJoinCheckTree1"
          label={
            <Trans
              i18nKey="group-management-app.join.terms-check"
              default="I have read and agree to the FamilySearch <0>Content Submission Agreement</0>, <1>Terms of Use</1>, and <2>Privacy Notice</2>."
            >
              <Link target="_blank" to="https://www.familysearch.org/legal/familysearch-content-submission-agreement" />
              <Link target="_blank" to="https://www.familysearch.org/legal/terms" />
              <Link target="_blank" to="https://www.familysearch.org/legal/privacy" />
            </Trans>
          }
        />
        <FormCheckbox
          data-testid="agree-join-checkbox-tree2"
          name="agreeJoinCheckTree2"
          label={t(
            'group-management-app.join.right-check',
            'I confirm that I have the right and/or permission to share any personal information that I provide access to within this group.'
          )}
        />
        <Separator size="xxs" />
        <Paragraph size="xs">
          <Trans
            i18nKey="join-group-overlay.join-group-privacy"
            default="By joining, you agree to share your contact name and portrait with group members and those who are invited to join the group."
          >
            <Link target="_blank" to="https://www.familysearch.org/legal/familysearch-content-submission-agreement" />
            <Link target="_blank" to="https://www.familysearch.org/legal/terms" />
            <Link target="_blank" to="https://www.familysearch.org/legal/privacy" />
          </Trans>
        </Paragraph>
      </>
    </ZionForm>
  )
}
